"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useEffect, useRef } from "react";
import { multiPartUpload } from "@/components/waves/create-wave/services/multiPartUpload";
export interface MediaUploadItem {
  id: string;
  file: File;
  previewUrl: string;
  serverUrl: string | null;
  status: "uploading" | "done" | "error";
  progress: number;
  error?: string;
}
interface UseMediaUploadReturn {
  items: MediaUploadItem[];
  addFiles: (files: File[]) => Promise<void>;
  removeItem: (id: string) => void;
  getServerUrls: () => string[];
  isUploading: boolean;
  hasErrors: boolean;
}

/**
 * Hook for managing multiple media file uploads
 */
export function useMediaUpload(t0) {
  const $ = _c(20);
  const maxFiles = t0 === undefined ? 4 : t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [items, setItems] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const previewUrlsRef = useRef(t2);
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const previewUrls = previewUrlsRef.current;
      return () => {
        previewUrls.forEach(_temp);
        previewUrls.clear();
      };
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] !== items.length || $[5] !== maxFiles) {
    t5 = async files => {
      const availableSlots = maxFiles - items.length;
      const filesToAdd = files.slice(0, availableSlots);
      if (filesToAdd.length === 0) {
        return;
      }
      const newItems = filesToAdd.map(file => {
        const previewUrl = URL.createObjectURL(file);
        previewUrlsRef.current.add(previewUrl);
        return {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          file,
          previewUrl,
          serverUrl: null,
          status: "uploading" as const,
          progress: 0
        };
      });
      setItems(prev => [...prev, ...newItems]);
      for (const item of newItems) {
        ;
        try {
          const result = await multiPartUpload({
            file: item.file,
            path: "drop",
            onProgress: progress => {
              setItems(prev_1 => prev_1.map(i_0 => i_0.id === item.id ? {
                ...i_0,
                progress
              } : i_0));
            }
          });
          setItems(prev_2 => prev_2.map(i_1 => i_1.id === item.id ? {
            ...i_1,
            serverUrl: result.url,
            status: "done" as const,
            progress: 100
          } : i_1));
        } catch (t6) {
          const error = t6;
          const errorMsg = error instanceof Error ? error.message : "Upload failed";
          if (item.previewUrl) {
            URL.revokeObjectURL(item.previewUrl);
            previewUrlsRef.current.delete(item.previewUrl);
          }
          setItems(prev_0 => prev_0.map(i => i.id === item.id ? {
            ...i,
            previewUrl: "",
            status: "error" as const,
            error: errorMsg
          } : i));
        }
      }
    };
    $[4] = items.length;
    $[5] = maxFiles;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const addFiles = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = id => {
      setItems(prev_3 => {
        const item_0 = prev_3.find(i_2 => i_2.id === id);
        if (item_0?.previewUrl) {
          URL.revokeObjectURL(item_0.previewUrl);
          previewUrlsRef.current.delete(item_0.previewUrl);
        }
        return prev_3.filter(i_3 => i_3.id !== id);
      });
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const removeItem = t6;
  let t7;
  if ($[8] !== items) {
    t7 = () => items.filter(_temp2).map(_temp3);
    $[8] = items;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const getServerUrls = t7;
  let t8;
  if ($[10] !== items) {
    t8 = items.some(_temp4);
    $[10] = items;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const isUploading = t8;
  let t9;
  if ($[12] !== items) {
    t9 = items.some(_temp5);
    $[12] = items;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const hasErrors = t9;
  let t10;
  if ($[14] !== addFiles || $[15] !== getServerUrls || $[16] !== hasErrors || $[17] !== isUploading || $[18] !== items) {
    t10 = {
      items,
      addFiles,
      removeItem,
      getServerUrls,
      isUploading,
      hasErrors
    };
    $[14] = addFiles;
    $[15] = getServerUrls;
    $[16] = hasErrors;
    $[17] = isUploading;
    $[18] = items;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}
function _temp5(i_7) {
  return i_7.status === "error";
}
function _temp4(i_6) {
  return i_6.status === "uploading";
}
function _temp3(i_5) {
  return i_5.serverUrl as string;
}
function _temp2(i_4) {
  return i_4.status === "done" && !!i_4.serverUrl;
}
function _temp(url) {
  URL.revokeObjectURL(url);
}